import { NumberFilter } from "@react3l/advanced-filters";
import { Skeleton } from "antd";
import { AxisResponse, Dashboard } from "models/Dashboard";
import React, { useEffect, useState } from "react";
import { dashboardRepository } from "repositories/dashboard-repository";
import { rankDeviceTypes } from "./dashboard-data";

const numberFormat = new Intl.NumberFormat("vi-VN");
const percentFormat = new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 });
// Loại rất nhỏ (Loa 17 / 352.893) ghi "<1%" thay vì "0%", để không đọc thành không có thiết bị nào.
const formatShare = (ratio: number) => (ratio > 0 && ratio < 0.01 ? "<1%" : `${percentFormat.format(ratio * 100)}%`);

interface DeviceTypeBarsProps {
  filter: Dashboard;
}

export default function DeviceTypeBars({ filter }: DeviceTypeBarsProps) {
  const [data, setData] = useState<AxisResponse>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const subscription = dashboardRepository
      .statisticDevice({
        time: new NumberFilter({ equal: filter.time }),
        type: new NumberFilter({ equal: 1 }),
        skip: 0,
        take: 1,
      })
      .subscribe({
        next: (response: AxisResponse) => {
          setData(response);
          setLoading(false);
        },
        error: () => setLoading(false),
      });
    return () => subscription.unsubscribe();
  }, [filter.time]);

  if (loading) return <Skeleton active paragraph={{ rows: 5 }} />;

  const rows = rankDeviceTypes(data);
  if (rows.length === 0) return <p className="dashboard-master__empty">Chưa có dữ liệu thiết bị trong kỳ này</p>;

  // Nhóm còn lại (chưa xác định loại, hoặc gộp loại nhỏ) thường lớn hơn mọi loại có tên: đặt cuối sau vạch ngăn,
  // không vẽ thanh để thang tính theo loại có tên lớn nhất. Mọi dòng cùng ghi "số · % tổng" để so được với nhau.
  const named = rows.filter((row) => !row.isRest);
  const rest = rows.find((row) => row.isRest);
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const max = Math.max(...named.map((row) => row.value), 0);
  const share = (value: number) => (
    <span className="device-bars__value tabular-nums">
      {numberFormat.format(value)}
      <span className="device-bars__sep" aria-hidden />
      <span className="device-bars__share">{formatShare(value / total)}</span>
    </span>
  );
  return (
    <div className="device-bars">
      <ul className="device-bars__list">
        {named.map((row) => (
          <li key={row.name} className="device-bars__row" title={`${row.name}: ${numberFormat.format(row.value)}`}>
            <span className="device-bars__name">{row.name}</span>
            <span className="device-bars__track" aria-hidden>
              <span className="device-bars__fill" style={{ width: `${(row.value / max) * 100}%` }} />
            </span>
            {share(row.value)}
          </li>
        ))}
      </ul>
      {rest && (
        <div className="device-bars__rest">
          <span className="device-bars__name">{rest.name}</span>
          {share(rest.value)}
        </div>
      )}
      <div className="device-bars__total">
        <span>Tổng</span>
        <span className="tabular-nums">{numberFormat.format(total)}</span>
      </div>
    </div>
  );
}
